import {
  createHttpLink,
  from,
  ApolloClient,
  InMemoryCache,
  MutationOptions,
  NetworkStatus,
  OperationVariables,
  QueryOptions,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError, ErrorResponse } from '@apollo/client/link/error';

import { APIORIGIN } from '../../environment';

export const isLoading = (loading: boolean, networkStatus: NetworkStatus): boolean =>
  loading || networkStatus === NetworkStatus.refetch;

const httpLink = createHttpLink({
  uri: `${APIORIGIN}/query`,
});

interface Context {
  headers: Record<string, unknown>;
}

const authLink = setContext((_, { headers }: Context) => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const AUTH_FAILED = 'AUTH_FAILED';

const logoutLink = onError(({ graphQLErrors }: ErrorResponse) => {
  const authFailed = graphQLErrors?.some(
    ({ extensions }) => typeof extensions !== 'undefined' && extensions.Code === AUTH_FAILED
  );
  if (authFailed && !window.location.pathname.startsWith('/login')) {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }
});

const apolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([authLink, logoutLink, httpLink]),

  name: 'adeptdc-react',
  version: '1.0.0',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const query = async <T = any, TVariables = OperationVariables>(
  options: QueryOptions<TVariables>
): Promise<T | null | undefined> => {
  const { data, errors } = await apolloClient.query<T, TVariables>(options);
  if (errors && errors.length > 0) {
    const errMsg = errors.map(({ message }) => message).join(', ');
    throw new Error(errMsg);
  }

  return data;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mutate = async <T = any, TVariables = OperationVariables>(
  options: MutationOptions<T, TVariables>
): Promise<T | null | undefined> => {
  const { data, errors } = await apolloClient.mutate<T, TVariables>(options);
  if (errors && errors.length > 0) {
    const errMsg = errors.map(({ message }) => message).join(', ');
    throw new Error(errMsg);
  }

  return data;
};

export default apolloClient;
